import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {TextButtonWithConfirmation} from 'shells/button/text-button-with-confirmation/text-button-with-confirmation';
import {colors} from 'shells/colors/colors';
import {WarningIcon} from 'shells/icon/svg/warning-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {Table} from 'shells/table/table';
import type {SpecialState} from 'shells/table/types';
import {Body} from 'shells/typography/body';

import type {GetAssignments_meeting_assignments_edges_meetings as AssignedMeeting} from 'nutshell-graphql-types';

import {getStatusBadge} from '../helpers';

import {UnassignActionMenu} from './unassign-action-menu';

type Props = {
    assignedMeetings: AssignedMeeting[];
    firstRow?: React.ReactElement<any> | null | undefined;
    secondRow?: React.ReactElement<any> | null | undefined;
    specialStates?: SpecialState[];
    onUnassign?: (assigneeId: string) => void;
    onResetSeveredMeeting?: (severedMeetingId: string) => void;
};

type CellType = {
    cell: {
        value: AssignedMeeting;
    };
};

export function AssignmentTable(props: Props) {
    const {onUnassign, onResetSeveredMeeting} = props;

    const columns = React.useMemo(() => {
        const unassignColumn = [
            {
                id: 'action',
                // @ts-expect-error
                accessor: (row) => row,
                dataType: 'component',
                maxWidth: 40,
                width: 40,
                minWidth: 40,
                rightAlign: true,
                disableSortBy: true,
                Cell: ({cell}: CellType) => {
                    return onUnassign ? (
                        <UnassignActionMenu
                            // @ts-expect-error
                            assigneeToUnassign={cell.value.hosts[0].name}
                            onConfirm={() => {
                                // @ts-expect-error
                                onUnassign(cell.value.hosts[0].id);
                            }}
                        />
                    ) : (
                        // Need this so all table columns are aligned
                        <div></div>
                    );
                },
            },
        ];

        return [
            {
                id: 'assignee',
                // @ts-expect-error
                accessor: (row) => row,
                dataType: 'linkedName',
                maxWidth: 150,
                width: 150,
                minWidth: 150,
                shouldTruncate: true,
                Cell: ({cell}: CellType) => {
                    // @ts-expect-error
                    const name = cell.value.hosts[0].name;

                    return <span title={name}>{name}</span>;
                },
            },
            {
                id: 'status',
                // @ts-expect-error
                accessor: (row) => getStatusBadge(row),
                dataType: 'statusBadge',
                maxWidth: 175,
                width: 175,
                minWidth: 175,
            },
            {
                id: 'bookings',
                accessor: (row: AssignedMeeting) => row.bookings.length,
                dataType: 'component',
                width: 115,
                maxWidth: 115,
                minWidth: 115,
                Cell: ({cell}: any) => getBookingsLink(cell.value, cell.row.original.htmlUrlPath),
            },
            {
                id: 'severed',
                // @ts-expect-error
                accessor: (row) => row,
                dataType: 'component',
                width: 125,
                maxWidth: 125,
                minWidth: 125,
                Cell: ({cell}: CellType) =>
                    cell.value.isSevered && onResetSeveredMeeting ? (
                        <StackLegacy isHorizontal={true} alignCenter={true} spacing={12}>
                            <StackLegacy
                                isHorizontal={true}
                                alignCenter={true}
                                spacing={4}
                                // @ts-expect-error
                                justifyCenter={true}
                            >
                                <WarningIcon
                                    size={16}
                                    alignCenter={true}
                                    // @ts-expect-error
                                    color={colors.orangeAttention}
                                />
                                <Body size='small' color='warn'>
                                    Modified
                                </Body>
                            </StackLegacy>
                            <TextButtonWithConfirmation
                                // @ts-expect-error
                                size='small'
                                buttonVariant='text-primary'
                                buttonText='Reset'
                                primaryText='Yes, reset meeting'
                                secondaryText='No, cancel'
                                question="Are you certain you want to reset this user's managed meeting to its original state?"
                                onConfirm={() => onResetSeveredMeeting(cell.value.id)}
                            />
                        </StackLegacy>
                    ) : null,
            },
            ...unassignColumn,
        ];
    }, [onUnassign, onResetSeveredMeeting]);

    return (
        <Table
            firstRow={props.firstRow}
            secondRow={props.secondRow}
            hasBorders={true}
            fullWidth={true}
            // @ts-expect-error
            columns={columns}
            isCompact={true}
            hasHeaders={false}
            data={props.assignedMeetings}
            specialStates={props.specialStates}
        />
    );
}

function getBookingsLink(bookingCount: number, htmlUrlPath: string) {
    const term = bookingCount === 1 ? 'booking' : 'bookings';

    return (
        // @ts-expect-error
        <Link to={`${htmlUrlPath}/bookings`} as={RoutedLink} variant='primary' size='small'>
            {bookingCount.toLocaleString()} {term}
        </Link>
    );
}
